import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "snowfall";

const DEFAULT_SKY_TOP = "#0f1d36";
const DEFAULT_SKY_BOTTOM = "#1f2f4d";
const DEFAULT_FLAKE = "#f8fafc";

const FLAKE_DENSITY = 0.0003;     // flakes per pixel² of canvas
const MIN_FLAKES = 70;
const MAX_FLAKES = 320;
const WIND_BASE = 0.1;            // baseline horizontal drift
const SWAY_AMPLITUDE = 0.6;       // sinusoidal sway amplitude (px)

function rand(min, max) { return min + Math.random() * (max - min); }

export default function Snowfall({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const flakesRef = useRef([]);
  const colorsRef = useRef([DEFAULT_SKY_TOP, DEFAULT_SKY_BOTTOM, DEFAULT_FLAKE]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_SKY_TOP),
      pickColor(colors, 1, DEFAULT_SKY_BOTTOM),
      pickColor(colors, 2, DEFAULT_FLAKE),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    let t = 0;

    const seedFlakes = () => {
      const target = Math.round(width * height * FLAKE_DENSITY);
      const count = Math.max(MIN_FLAKES, Math.min(MAX_FLAKES, target));
      const arr = new Array(count);
      for (let i = 0; i < count; i += 1) {
        // depth: 0 = far/small/slow/dim, 1 = near/big/fast/bright.
        // Distributing depth uniformly gives a believable parallax mix.
        const depth = Math.random();
        const radius = 0.6 + depth * 2.4;       // 0.6 .. 3.0 px
        const speed = 0.4 + depth * 1.5;        // px/frame
        arr[i] = {
          x: rand(0, width),
          y: rand(-height, height),
          radius,
          depth,
          vy: speed,
          // Per-flake sway phase / period so the column of falling flakes
          // doesn't look like a metronome.
          swayPhase: rand(0, Math.PI * 2),
          swayPeriod: rand(0.4, 1.2),
          // A small fraction of flakes are drawn as 6-spoked snowflakes
          // instead of disks — adds visual interest without being too busy.
          star: depth > 0.7 && Math.random() < 0.35,
          // Star flakes spin slowly.
          spin: rand(-0.02, 0.02),
          angle: rand(0, Math.PI * 2),
        };
      }
      // Sort back-to-front so near flakes draw on top.
      arr.sort((a, b) => a.depth - b.depth);
      flakesRef.current = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedFlakes();
    };

    const drawSnowflake = (cx, cy, r, angle, alpha, color) => {
      // Six-spoke star. Stroked rather than filled to keep it light.
      ctx.strokeStyle = rgba(color, alpha);
      ctx.lineWidth = Math.max(0.6, r * 0.35);
      ctx.lineCap = "round";
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle);
      for (let i = 0; i < 6; i += 1) {
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(0, -r * 1.6);
        // Tiny crossbars on each spoke — enough hint to read as a flake.
        ctx.moveTo(-r * 0.5, -r * 0.9);
        ctx.lineTo(r * 0.5, -r * 0.9);
        ctx.stroke();
        ctx.rotate(Math.PI / 3);
      }
      ctx.restore();
    };

    const tick = () => {
      const [skyTop, skyBottom, flakeColor] = colorsRef.current;

      // Sky gradient.
      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, skyTop);
      grad.addColorStop(1, skyBottom);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      const flakes = flakesRef.current;
      for (let i = 0; i < flakes.length; i += 1) {
        const f = flakes[i];
        // Vertical fall + sinusoidal sway → curved trajectories. Wind has a
        // small global bias (left-to-right) that scales with depth so near
        // flakes feel pushed harder by the breeze.
        f.y += f.vy;
        const sway = Math.sin(t * f.swayPeriod + f.swayPhase) * SWAY_AMPLITUDE;
        f.x += sway + WIND_BASE * f.depth;
        f.angle += f.spin;

        // Wrap once a flake goes past the bottom or off the sides.
        if (f.y > height + 8) {
          f.y = -8;
          f.x = rand(0, width);
        }
        if (f.x < -8) f.x = width + 8;
        if (f.x > width + 8) f.x = -8;

        // Near flakes are brighter. Far flakes are dim / softer.
        const alpha = 0.35 + f.depth * 0.55;
        if (f.star) {
          drawSnowflake(f.x, f.y, f.radius * 1.4, f.angle, alpha, flakeColor);
        } else {
          ctx.fillStyle = rgba(flakeColor, alpha);
          ctx.beginPath();
          ctx.arc(f.x, f.y, f.radius, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      t += 0.016;
      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
